import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { ChevronRight, ChevronDown, Plus, Pencil, Trash2, PackageSearch } from 'lucide-react';
import type { AdminCategoryView, CategoryDisplayType } from '../hooks/useAdminCategoriesQuery';

const MAX_DEPTH = 3;

/**
 * C-1(ID103) 전시 유형 → 트리 배지 라벨 폴백 맵.
 * C2 계약(be-o): 표시 라벨 SSoT 는 응답 `displayTypeLabel`(BE 공급). 여기 맵은 BE 가 라벨을
 * 미공급(codegen lag/구버전 응답)할 때만 쓰는 폴백이며, raw enum 직접노출은 하지 않는다.
 */
const DISPLAY_TYPE_LABEL_FALLBACK: Record<CategoryDisplayType, string> = {
  MUSIC: 'EVER MUSIC',
  MD: 'EVER MD',
  CON: 'EVER CON',
  FRIENDS: 'EVER FRIENDS',
  LIGHT_STICK: 'EVER LIGHT STICK',
  PRE_ORDER: '예약판매',
};

interface CategoryNodeProps {
  node: AdminCategoryView;
  selectedCategoryId?: string | null | undefined;
  onAddChild: (parent: AdminCategoryView) => void;
  onEdit: (category: AdminCategoryView) => void;
  onDelete: (category: AdminCategoryView) => void;
  /** ID43: [연결 상품 관리] 진입. */
  onManageProducts?: (category: AdminCategoryView) => void;
}

const CategoryNode = ({ node, selectedCategoryId, onAddChild, onEdit, onDelete, onManageProducts }: CategoryNodeProps) => {
  const { t } = useTranslation('common');
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const canAddChild = node.depth < MAX_DEPTH;
  const indentPx = (node.depth - 1) * 20;
  const isSelected = selectedCategoryId === node.categoryId;

  return (
    <div>
      <div
        className="group flex items-center gap-1 rounded-lg px-2 py-1.5 transition-colors hover:bg-[var(--surface-1)]"
        style={{
          paddingLeft: `${indentPx + 8}px`,
          ...(isSelected ? { background: 'var(--surface-1)' } : {}),
        }}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded"
          style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
          aria-label={expanded ? t('cms.categories.collapse') : t('cms.categories.expand')}
        >
          {expanded
            ? <ChevronDown size={14} style={{ color: 'var(--content-muted)' }} />
            : <ChevronRight size={14} style={{ color: 'var(--content-muted)' }} />}
        </button>

        <div className="flex flex-1 items-center gap-2 min-w-0">
          <span
            className="truncate text-sm font-medium"
            style={{ color: node.active ? 'var(--content-strong)' : 'var(--content-muted)' }}
          >
            {node.name}
          </span>
          {/* 숨김(active=false) — 고객 화면에 안 보이는 카테고리. 삭제가 아니다(삭제는 BE hard delete). */}
          {!node.active ? (
            <span
              className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold shrink-0"
              style={{ background: 'var(--surface-1)', color: 'var(--content-muted)', border: '1px solid var(--divider-strong)' }}
              title={t('cms.categories.hiddenBadgeAria', { defaultValue: '고객 화면에 노출되지 않는 카테고리' })}
              data-testid="category-hidden-badge"
            >
              {t('cms.categories.hiddenBadge', { defaultValue: '숨김' })}
            </span>
          ) : null}
          <span className="font-mono text-xs shrink-0" style={{ color: 'var(--content-muted)' }}>
            {node.categoryCode}
          </span>
          {/* C-1(ID103) 전시 유형 배지 — 지정된 카테고리만 노출. SF 서브탭 매핑 확인용.
              C2 계약: BE displayTypeLabel(SSoT) 우선, 미공급 시 폴백 맵(raw enum 직접노출 금지). */}
          {node.displayType ? (
            <span
              className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold shrink-0"
              style={{ background: 'var(--accent-soft)', color: 'var(--accent-default)' }}
            >
              {node.displayTypeLabel ?? DISPLAY_TYPE_LABEL_FALLBACK[node.displayType]}
            </span>
          ) : null}
          {/* 이슈1 — B2B 전용 배지. 이 카테고리(+하위·소속 상품)는 B2B(기업) 회원에게만 노출. */}
          {node.b2bOnly ? (
            <span
              className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold shrink-0"
              style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
              title={t('cms.categories.b2bOnlyBadgeAria', { defaultValue: 'B2B(기업) 회원 전용 노출' })}
            >
              {t('cms.categories.b2bOnlyBadge', { defaultValue: 'B2B 전용' })}
            </span>
          ) : null}
          <span
            className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium shrink-0"
            style={
              node.active
                ? { background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' }
                : { background: 'var(--surface-1)', color: 'var(--content-muted)' }
            }
          >
            D{node.depth}
          </span>
        </div>

        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {onManageProducts && (
            <button
              type="button"
              onClick={() => onManageProducts(node)}
              className="flex h-6 w-6 items-center justify-center rounded hover:bg-[var(--surface-raised)]"
              title={t('cms.categories.products.manage', '연결 상품 관리')}
              aria-label={t('cms.categories.products.manageAria', { name: node.name, defaultValue: t('cms.categories.products.manage', '연결 상품 관리') })}
            >
              <PackageSearch size={13} style={{ color: isSelected ? 'var(--cta-primary)' : 'var(--content-muted)' }} />
            </button>
          )}
          {canAddChild && (
            <button
              type="button"
              onClick={() => onAddChild(node)}
              className="flex h-6 w-6 items-center justify-center rounded hover:bg-[var(--surface-raised)]"
              title={t('cms.categories.addChild')}
              aria-label={t('cms.categories.addChild')}
            >
              <Plus size={13} style={{ color: 'var(--accent-default)' }} />
            </button>
          )}
          <button
            type="button"
            onClick={() => onEdit(node)}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-[var(--surface-raised)]"
            title={t('cms.edit')}
            aria-label={t('cms.edit')}
          >
            <Pencil size={13} style={{ color: 'var(--content-muted)' }} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(node)}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-[var(--surface-raised)]"
            title={t('cms.delete')}
            aria-label={t('cms.delete')}
          >
            <Trash2 size={13} style={{ color: 'var(--status-danger-fg)' }} />
          </button>
        </div>
      </div>

      {expanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <CategoryNode
              key={child.categoryId}
              node={child}
              selectedCategoryId={selectedCategoryId}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
              {...(onManageProducts ? { onManageProducts } : {})}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface CategoryTreeProps {
  roots: AdminCategoryView[];
  selectedCategoryId?: string | null | undefined;
  onAddChild: (parent: AdminCategoryView) => void;
  onEdit: (category: AdminCategoryView) => void;
  onDelete: (category: AdminCategoryView) => void;
  onManageProducts?: (category: AdminCategoryView) => void;
}

// B-2: 삭제 = BE hard delete(CategoryCommandService.delete — 하위·연결 상품이 없을 때만 행 삭제),
// active=false = 숨김(고객 화면 비노출). 숨김 카테고리도 운영자가 다시 활성화하거나 소속 상품을 관리해야 하므로
// 트리에 그대로 보이고("숨김" 배지), "숨김 카테고리 보기" 를 끌 때만 하위까지 뺀다(기본 보기).
// 종전엔 active=false 를 삭제로 보고 늘 뺐다 — ETC·china·SALES PROMOTION 등 숨김 6개(연결 상품 약 6,000건)가 사라졌다.
const withoutHidden = (nodes: AdminCategoryView[]): AdminCategoryView[] =>
  nodes
    .filter((node) => node.active)
    .map((node) => ({ ...node, children: withoutHidden(node.children) }));

const countHidden = (nodes: AdminCategoryView[]): number =>
  nodes.reduce((n, node) => n + (node.active ? 0 : 1) + countHidden(node.children), 0);

export const CategoryTree = ({
  roots,
  selectedCategoryId,
  onAddChild,
  onEdit,
  onDelete,
  onManageProducts,
}: CategoryTreeProps) => {
  const { t } = useTranslation('common');
  const [showHidden, setShowHidden] = useState(true);
  const hiddenCount = countHidden(roots);

  const visibleRoots = showHidden ? roots : withoutHidden(roots);

  const hiddenToggle =
    hiddenCount > 0 ? (
      <label className="mb-2 flex items-center justify-end gap-2 text-xs" style={{ color: 'var(--content-default)' }}>
        <input
          type="checkbox"
          checked={showHidden}
          onChange={(e) => setShowHidden(e.target.checked)}
          data-testid="category-show-hidden"
        />
        {t('cms.categories.showHidden', { defaultValue: '숨김 카테고리 보기 ({{count}})', count: hiddenCount })}
      </label>
    ) : null;

  if (visibleRoots.length === 0) {
    return (
      <>
        {hiddenToggle}
        <p className="py-12 text-center text-sm" style={{ color: 'var(--content-muted)' }}>
          {t('cms.categories.empty')}
        </p>
      </>
    );
  }

  return (
    <>
    {hiddenToggle}
    <div
      className="rounded-xl"
      style={{ border: '1px solid var(--divider-strong)', background: 'var(--surface-raised)' }}
    >
      {visibleRoots.map((root, idx) => (
        <div
          key={root.categoryId}
          style={idx < visibleRoots.length - 1 ? { borderBottom: '1px solid var(--divider-strong)' } : undefined}
          className="p-1"
        >
          <CategoryNode
            node={root}
            selectedCategoryId={selectedCategoryId}
            onAddChild={onAddChild}
            onEdit={onEdit}
            onDelete={onDelete}
            {...(onManageProducts ? { onManageProducts } : {})}
          />
        </div>
      ))}
    </div>
    </>
  );
};
